import React, { useEffect, createContext, useContext, useCallback } from 'react';
import posthog from 'posthog-js';

const PostHogContext = createContext(null);

const PostHogProvider = ({ children }) => {
  const isAnalyticsEnabled = process.env.REACT_APP_ENABLE_ANALYTICS !== 'false';

  useEffect(() => {
    const posthogKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
    const posthogHost = process.env.REACT_APP_PUBLIC_POSTHOG_HOST;

    if (!isAnalyticsEnabled) {
      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog analytics disabled via REACT_APP_ENABLE_ANALYTICS');
      }
      return;
    }

    if (posthogKey && posthogHost && !posthog.__loaded) {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        defaults: '2025-11-30',
        loaded: (ph) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('PostHog initialized for onboarding routes');
          }
        },
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        // Session replay configuration
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-ph-mask]',
        },
        // Privacy settings for HIPAA compliance
        mask_all_text: false,
        mask_all_element_attributes: false,
        // Error tracking configuration
        capture_exceptions: true,
      });
    }

    return () => {
      if (isAnalyticsEnabled && posthogKey && posthogHost) {
        posthog.reset();
      }
    };
  }, [isAnalyticsEnabled]);

  // Identify user with PostHog
  const identifyUser = useCallback((userId, properties = {}) => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      posthog.identify(userId, properties);
    }
  }, [isAnalyticsEnabled]);

  // Capture custom events
  const captureEvent = useCallback((eventName, properties = {}) => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      posthog.capture(eventName, properties);
    }
  }, [isAnalyticsEnabled]);

  // Reset user identity (on logout)
  const resetUser = useCallback(() => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      posthog.reset();
    }
  }, [isAnalyticsEnabled]);

  // Set user properties without identifying
  const setUserProperties = useCallback((properties) => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      posthog.people.set(properties);
    }
  }, [isAnalyticsEnabled]);

  // Track page view manually
  const trackPageView = useCallback((pageName, properties = {}) => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      posthog.capture('$pageview', { page: pageName, ...properties });
    }
  }, [isAnalyticsEnabled]);

  // Capture exceptions for error tracking
  const captureException = useCallback((error, additionalProperties = {}) => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      posthog.captureException(error, additionalProperties);
    }
  }, [isAnalyticsEnabled]);

  // Session replay controls
  const startSessionRecording = useCallback(() => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      posthog.startSessionRecording();
    }
  }, [isAnalyticsEnabled]);

  const stopSessionRecording = useCallback(() => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      posthog.stopSessionRecording();
    }
  }, [isAnalyticsEnabled]);

  // Get session ID for cross-domain tracking
  const getSessionId = useCallback(() => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      return posthog.get_session_id();
    }
    return null;
  }, [isAnalyticsEnabled]);

  // Get session replay URL
  const getSessionReplayUrl = useCallback(() => {
    if (isAnalyticsEnabled && posthog.__loaded) {
      return posthog.get_session_replay_url();
    }
    return null;
  }, [isAnalyticsEnabled]);

  const contextValue = {
    identifyUser,
    captureEvent,
    resetUser,
    setUserProperties,
    trackPageView,
    captureException,
    startSessionRecording,
    stopSessionRecording,
    getSessionId,
    getSessionReplayUrl,
    posthog,
  };

  return (
    <PostHogContext.Provider value={contextValue}>
      {children}
    </PostHogContext.Provider>
  );
};

// Hook to use PostHog analytics in components
export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (!context) {
    console.warn('usePostHog must be used within PostHogProvider');
    return {
      identifyUser: () => {},
      captureEvent: () => {},
      resetUser: () => {},
      setUserProperties: () => {},
      trackPageView: () => {},
      captureException: () => {},
      startSessionRecording: () => {},
      stopSessionRecording: () => {},
      getSessionId: () => null,
      getSessionReplayUrl: () => null,
      posthog: null,
    };
  }
  return context;
};

// Pre-defined event names for consistency
export const POSTHOG_EVENTS = {
  // Onboarding funnel events
  SIGNUP_STARTED: 'onboarding_signup_started',
  SIGNUP_STEP_COMPLETED: 'onboarding_step_completed',
  SIGNUP_COMPLETED: 'onboarding_signup_completed',
  
  // Organization events
  ORGANIZATION_CREATED: 'organization_created',
  ORGANIZATION_SEARCH: 'organization_search',
  
  // User events
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  USER_CREATED: 'user_created',
  
  // Feature selection
  FEATURE_SELECTED: 'feature_selected',
  FEATURES_SAVED: 'features_saved',
  
  // EHR events
  EHR_CONNECT_STARTED: 'ehr_connect_started',
  EHR_CONNECTED: 'ehr_connected',
  
  // Provider events
  PROVIDERS_UPLOAD_STARTED: 'providers_upload_started',
  PROVIDERS_UPLOADED: 'providers_uploaded',
  
  // Dashboard events
  DASHBOARD_VIEWED: 'dashboard_viewed',

  // Feedback events
  FEEDBACK_INLINE_SUBMITTED: 'feedback_inline_submitted',
  FEEDBACK_FIELD_VALIDATED: 'feedback_field_validated',
  FEEDBACK_MODAL_SHOWN: 'feedback_modal_shown',
  FEEDBACK_MODAL_SUBMITTED: 'feedback_modal_submitted',
  FEEDBACK_MODAL_SKIPPED: 'feedback_modal_skipped',
  FEEDBACK_TRIGGER_DECISION: 'feedback_trigger_decision',
  SESSION_MILESTONE: 'session_milestone',
};

export default PostHogProvider;
